// helpDocs.js — Complete, detailed platform documentation
const helpDocs = [
  // ─────────────────────────────────────────────────────────────────────────────
  // GETTING STARTED
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    color: 'bg-indigo-100 text-indigo-600',
    route: null,
    sections: [
      {
        heading: 'Welcome to XenBox',
        content: [
          { type: 'p', text: 'XenBox is your all-in-one business platform — customer support, CRM, project management, marketing, and AI automation under one roof. This guide gets you productive on day one.' },
        ]
      },
      {
        heading: 'Navigating the Sidebar',
        content: [
          { type: 'p', text: 'The left-hand sidebar is always visible and gives you instant access to every module. Click on any item to navigate there immediately.' },
          { type: 'list', text: '📊 Dashboard — Live overview of all your metrics' },
          { type: 'list', text: '🎙️ Huddle — Team meeting rooms' },
          { type: 'list', text: '💬 Inbox — Unified customer messaging' },
          { type: 'list', text: '🎫 Helpdesk — Customer support tickets' },
          { type: 'list', text: '👥 Contacts — Manage contacts and accounts' },
          { type: 'list', text: '🤝 CRM — Leads, opportunities, forecasting, and revenue intel' },
          { type: 'list', text: '📋 Projects — Tasks, boards, timesheets' },
          { type: 'list', text: '📅 Calendar — Your unified schedule' },
          { type: 'list', text: '🗓️ Appointments — Booking and scheduling' },
          { type: 'list', text: '📚 Knowledge Base — Articles and public help portal' },
          { type: 'list', text: '📣 Marketing — Campaigns and email templates' },
          { type: 'list', text: '⭐ CSAT — Customer satisfaction surveys' },
          { type: 'list', text: '🧠 Nexus AI — Intelligent automation agents' },
          { type: 'tip', text: 'Click the collapse arrow (◀) at the bottom of the sidebar to minimize it to icon-only mode, giving you more screen space.' },
        ]
      },
      {
        heading: 'The Top Header Bar',
        content: [
          { type: 'p', text: 'The header at the top of every page gives you platform-wide tools:' },
          { type: 'list', text: '← Back Arrow — Go back to the previous page' },
          { type: 'list', text: '🔍 Search Bar — Find tickets, contacts, articles, and users instantly across the entire platform. Type at least 2 characters to see live suggestions.' },
          { type: 'list', text: '+ Blue Button — Quick Create: open a shortcut menu to create a new Lead, Account, Deal, Ticket, Article, or invite a User without leaving your current page' },
          { type: 'list', text: '⏱ Timer — Shows any active time-tracking session running in the background' },
          { type: 'list', text: '🔔 Bell — Notification center: click to see all recent activity, mark items as read, or dismiss them' },
          { type: 'list', text: '? — This Help Center' },
          { type: 'list', text: '🌐 Globe — Language switcher (English / Arabic with RTL support)' },
          { type: 'list', text: '👤 Your Name — Profile dropdown: access your profile page or log out' },
        ]
      },
      {
        heading: 'Setting Up Your Profile',
        content: [
          { type: 'step', text: 'Click your name in the top-right corner.' },
          { type: 'step', text: 'Select "Profile" from the dropdown.' },
          { type: 'step', text: 'Upload a profile photo, update your display name, and verify your timezone.' },
          { type: 'step', text: 'Click "Save Changes".' },
          { type: 'tip', text: 'Your profile photo appears next to your name on tickets, tasks, comments, and in Huddle meetings — keep it recognizable!' },
        ]
      },
      {
        heading: 'Switching Language',
        content: [
          { type: 'step', text: 'Click the 🌐 globe icon in the top header.' },
          { type: 'step', text: 'Select English or العربية (Arabic).' },
          { type: 'p', text: 'The full interface immediately switches, including right-to-left layout for Arabic.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // DASHBOARD
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: '📊',
    color: 'bg-blue-100 text-blue-600',
    route: '/dashboard',
    sections: [
      {
        heading: 'What is the Dashboard?',
        content: [
          { type: 'p', text: 'The Dashboard is your home screen after login. It aggregates real-time data from all modules you have access to — giving you an immediate pulse on your business without clicking around.' },
        ]
      },
      {
        heading: 'Metric Cards',
        content: [
          { type: 'p', text: 'The top row of cards shows key numbers at a glance:' },
          { type: 'list', text: 'Open Tickets — Total unresolved customer support tickets' },
          { type: 'list', text: 'Active Projects — Projects currently underway' },
          { type: 'list', text: 'Conversations — Active Inbox threads' },
          { type: 'list', text: 'CSAT Score — Average customer satisfaction rating' },
          { type: 'tip', text: 'Click any metric card to jump directly into that module with the relevant data pre-filtered.' },
        ]
      },
      {
        heading: 'Performance Charts',
        content: [
          { type: 'p', text: 'The charts section shows your team\'s trends over time — ticket volumes, resolution times, project completion rates, and more. Hover over any data point on a chart to see the exact value for that date.' },
          { type: 'tip', text: 'Managers see team-wide aggregated data. Standard users see their own personal metrics and workload.' },
        ]
      },
      {
        heading: 'Activity Feed',
        content: [
          { type: 'p', text: 'The live activity feed on the right side shows recent actions across the platform: new tickets, completed tasks, messages received, huddles started, and more.' },
          { type: 'step', text: 'Click any item in the activity feed to navigate directly to that record.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // INBOX
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'inbox',
    title: 'Inbox',
    icon: '💬',
    color: 'bg-cyan-100 text-cyan-600',
    route: '/inbox',
    sections: [
      {
        heading: 'What is the Inbox?',
        content: [
          { type: 'p', text: 'The Inbox is your unified messaging center. All incoming customer messages — from email, live chat widget, or other connected channels — arrive here as Conversations. It works like a shared team inbox, giving every agent visibility while preventing duplicate responses.' },
        ]
      },
      {
        heading: 'Conversation List (Left Panel)',
        content: [
          { type: 'p', text: 'The left side shows all conversations sorted by most recent. Each row shows the contact name, a preview of the last message, and a timestamp.' },
          { type: 'list', text: 'Bold text = unread conversation with a new message' },
          { type: 'list', text: 'Blue dot = your conversation has a pending response' },
          { type: 'list', text: '"Mine" filter — shows only conversations assigned to you' },
          { type: 'list', text: '"Unassigned" filter — shows conversations not yet assigned to anyone' },
          { type: 'list', text: '"All" filter — shows every conversation across the team' },
        ]
      },
      {
        heading: 'Conversation Thread (Center)',
        content: [
          { type: 'p', text: 'Clicking a conversation opens the full message thread in the center panel. You see the complete history of messages between the customer and your team.' },
          { type: 'step', text: 'Type your reply in the message box at the bottom.' },
          { type: 'step', text: 'Use the toolbar to format text (bold, italic, bullet lists, links).' },
          { type: 'step', text: 'Click "Send" or press Ctrl+Enter / Cmd+Enter.' },
          { type: 'tip', text: 'Switch to the "Internal Note" tab in the reply box to write a message that only your team can see — perfect for briefing a colleague before they respond.' },
        ]
      },
      {
        heading: 'Conversation Details (Right Panel)',
        content: [
          { type: 'p', text: 'The right side panel shows information about the conversation and the contact:' },
          { type: 'list', text: 'Assignee — Who is responsible for responding. Click to reassign.' },
          { type: 'list', text: 'Status — Open, Pending, or Resolved. Click to change.' },
          { type: 'list', text: 'Contact Info — The customer\'s name, email, phone, and company.' },
          { type: 'list', text: 'Previous Conversations — A history of all past interactions with this contact.' },
          { type: 'list', text: 'Labels — Tags you can add to categorize the conversation (e.g., "Billing", "Bug").' },
        ]
      },
      {
        heading: 'Resolving a Conversation',
        content: [
          { type: 'step', text: 'Once the issue is resolved, click the green "Resolve" button at the top of the thread.' },
          { type: 'p', text: 'The conversation moves out of the active queue and into the archive. You can always search for it or filter by "Resolved" status.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // HELPDESK
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'helpdesk',
    title: 'Helpdesk',
    icon: '🎫',
    color: 'bg-blue-100 text-blue-600',
    route: '/tickets',
    sections: [
      {
        heading: 'What is the Helpdesk?',
        content: [
          { type: 'p', text: 'The Helpdesk manages all customer support requests as "Tickets". Each ticket has a full history of conversations, status tracking, SLA timers, priority levels, and assignees. Managers can also configure categories, teams, and SLA policies.' },
        ]
      },
      {
        heading: 'Menu: Dashboard',
        content: [
          { type: 'p', text: 'The Dashboard tab gives managers an overview of the support queue:' },
          { type: 'list', text: 'Total open, pending, and resolved tickets' },
          { type: 'list', text: 'Average first response time' },
          { type: 'list', text: 'Average resolution time' },
          { type: 'list', text: 'SLA breach rate' },
          { type: 'list', text: 'Agent workload distribution' },
          { type: 'tip', text: 'Visible to Managers and Admins only.' },
        ]
      },
      {
        heading: 'Menu: Tickets',
        content: [
          { type: 'p', text: 'The Tickets tab is the main working area. It lists all support tickets and lets you create, filter, and manage them.' },
          { type: 'list', text: 'List View — A detailed table of all tickets with columns for ID, subject, status, priority, assignee, and creation date' },
          { type: 'list', text: 'Grid View — A compact card-based view, useful for quick scanning' },
          { type: 'step', text: 'Click "+ New Ticket" to open the ticket creation form.' },
          { type: 'step', text: 'Fill in: Subject (required), Description, Customer/Contact, Category, Priority, and Assignee.' },
          { type: 'step', text: 'Click "Create Ticket". The customer gets an automatic email notification.' },
          { type: 'tip', text: 'Use the column filters (Status, Priority, Assignee) at the top of the list to narrow down the tickets shown.' },
        ]
      },
      {
        heading: 'Ticket Detail Page',
        content: [
          { type: 'p', text: 'Clicking any ticket opens its detail page with the full conversation thread and metadata:' },
          { type: 'list', text: 'Reply Tab — Type a response to the customer' },
          { type: 'list', text: 'Internal Note Tab — Leave a note only your team can see' },
          { type: 'list', text: 'Status Badge — Click to change status (Open → In Progress → Pending → Resolved → Closed)' },
          { type: 'list', text: 'Priority Badge — Click to change priority (Urgent, High, Medium, Low)' },
          { type: 'list', text: 'Assignee Field — Reassign to any team member or team' },
          { type: 'list', text: 'SLA Timer — Countdown showing time remaining before SLA breach' },
          { type: 'list', text: 'Attachments — Upload files or screenshots to the ticket' },
          { type: 'list', text: 'Merge — Merge this ticket with a duplicate ticket' },
          { type: 'list', text: 'Work Log — See a record of all time spent on this ticket' },
        ]
      },
      {
        heading: 'Menu: Reports',
        content: [
          { type: 'p', text: 'The Reports tab (visible to managers) provides analytics on your support team\'s performance over a selected time period:' },
          { type: 'list', text: 'Ticket volume trends (created vs resolved per day/week)' },
          { type: 'list', text: 'Resolution time breakdown by agent and category' },
          { type: 'list', text: 'SLA compliance percentage' },
          { type: 'list', text: 'Ticket distribution by category and priority' },
        ]
      },
      {
        heading: 'Menu: Configuration → SLA',
        content: [
          { type: 'p', text: 'SLAs (Service Level Agreements) define how quickly tickets must be responded to and resolved, based on priority.' },
          { type: 'step', text: 'Go to Configuration → SLA.' },
          { type: 'step', text: 'Click "+ New SLA Policy" to create one.' },
          { type: 'step', text: 'Set the policy name and define time targets for each priority level (e.g., Urgent tickets must be responded to within 1 hour).' },
          { type: 'step', text: 'Assign the SLA policy to specific categories or leave it as the default.' },
          { type: 'warning', text: 'When a ticket\'s SLA timer turns red on the ticket list, it means a breach is imminent — act immediately.' },
        ]
      },
      {
        heading: 'Menu: Configuration → Category',
        content: [
          { type: 'p', text: 'Categories organize your tickets by type (e.g., "Billing", "Technical Support", "Account Issue"). They help route tickets to the right team and generate meaningful reports.' },
          { type: 'step', text: 'Go to Configuration → Category.' },
          { type: 'step', text: 'Click "+ Add Category", enter a name, and optionally assign it to a default Team.' },
          { type: 'step', text: 'Save. The category will now appear in the ticket creation form.' },
        ]
      },
      {
        heading: 'Menu: Configuration → Team',
        content: [
          { type: 'p', text: 'Teams group agents together (e.g., "Tier 1 Support", "Billing Specialists"). Tickets can be assigned to a team, which alerts all members.' },
          { type: 'step', text: 'Go to Configuration → Team.' },
          { type: 'step', text: 'Click "+ New Team", give it a name, and add team members.' },
          { type: 'step', text: 'Optionally assign a default SLA policy to this team.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CONTACTS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'contacts',
    title: 'Contacts',
    icon: '👥',
    color: 'bg-emerald-100 text-emerald-600',
    route: '/contacts',
    sections: [
      {
        heading: 'What is the Contacts Module?',
        content: [
          { type: 'p', text: 'The Contacts module is your central workspace for managing all individual contact persons and business account records. It features two dedicated submenus: Contacts and Accounts.' },
        ]
      },
      {
        heading: 'Menu: Contacts',
        content: [
          { type: 'p', text: 'The Contacts tab lists every individual contact registered in the system.' },
          { type: 'list', text: 'List & Grid Views — Toggle between table view and grid cards using the view icons.' },
          { type: 'list', text: 'Search & Filters — Easily search by name, email, or filter by status and type.' },
          { type: 'step', text: 'Click "+ New Contact" to open the contact creation form.' },
          { type: 'step', text: 'Enter Contact Name, Email (required), Phone, Partner Type (Individual or Company), Parent Company, and Address.' },
          { type: 'step', text: 'Click "Create Now" to save the new contact.' },
          { type: 'p', text: 'Clicking any contact opens its detailed profile view including company affiliations, contact details, notes, and activity chatter timeline.' },
        ]
      },
      {
        heading: 'Menu: Accounts',
        content: [
          { type: 'p', text: 'The Accounts tab displays all company and organizational accounts.' },
          { type: 'list', text: 'Account Directory — View company names, websites, emails, phone numbers, and market regions.' },
          { type: 'step', text: 'Click on any account row to open the complete Account detail view.' },
          { type: 'p', text: 'The Account detail view provides an Account Health Score, linked contacts, active deals, support tickets, tasks, and interaction history.' },
        ]
      },
      {
        heading: 'Contact & Account Operations',
        content: [
          { type: 'step', text: 'Editing Profiles: Click "Edit" on a Contact or Account page to update details anytime.' },
          { type: 'step', text: 'Parent Company Linking: Link individual contacts to a parent company account for organization.' },
          { type: 'step', text: 'Attachments: Upload documents and files directly to a contact or account record.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CRM
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'crm',
    title: 'CRM',
    icon: '🤝',
    color: 'bg-green-100 text-green-600',
    route: '/crm',
    sections: [
      {
        heading: 'What is the CRM?',
        content: [
          { type: 'p', text: 'The CRM (Customer Relationship Management) module manages every business relationship — from the first moment a prospect discovers you, through the sales pipeline, to long-term accounts. It is organized into dedicated sections accessible from the top navigation tabs.' },
        ]
      },
      {
        heading: 'Menu: Dashboard',
        content: [
          { type: 'p', text: 'The CRM Dashboard gives sales managers an overview of the entire pipeline at a glance:' },
          { type: 'list', text: 'Total leads and their stage distribution' },
          { type: 'list', text: 'Opportunities pipeline value by stage' },
          { type: 'list', text: 'Win/loss ratio over the selected period' },
          { type: 'list', text: 'Top performing sales representatives' },
          { type: 'list', text: 'Recent activity feed (calls, meetings, notes)' },
        ]
      },
      {
        heading: 'Menu: Tasks',
        content: [
          { type: 'p', text: 'CRM Tasks are follow-up actions linked to contacts, leads, or deals — for example, "Call back John on Friday" or "Send proposal to Acme Corp".' },
          { type: 'step', text: 'Click "+ New Task" to create a CRM task.' },
          { type: 'step', text: 'Link it to a Contact, Lead, or Deal, set a due date, and assign it to a team member.' },
          { type: 'tip', text: 'CRM tasks with due dates appear on the assignee\'s Calendar automatically.' },
        ]
      },
      {
        heading: 'Menu: Contacts',
        content: [
          { type: 'p', text: 'Contacts is a directory of every individual person your company interacts with — customers, prospects, and partners.' },
          { type: 'step', text: 'Click "+ New Contact" to add someone.' },
          { type: 'step', text: 'Fill in: Full Name (required), Email, Phone, Job Title, Company/Account, and any additional notes.' },
          { type: 'step', text: 'Click "Save".' },
          { type: 'p', text: 'Each Contact profile shows all linked deals, past tickets, emails sent, meetings held, and notes — a complete 360° view of that relationship.' },
        ]
      },
      {
        heading: 'Menu: Sales → Leads',
        content: [
          { type: 'p', text: 'Leads represent potential customers who have not yet been qualified. They are at the very top of the sales funnel.' },
          { type: 'list', text: 'New — Just captured, not yet contacted' },
          { type: 'list', text: 'Contacted — You have reached out' },
          { type: 'list', text: 'Qualified — Confirmed there is a potential deal' },
          { type: 'list', text: 'Lost — Not interested or not a fit' },
          { type: 'step', text: 'Click "+ New Lead" to create one.' },
          { type: 'step', text: 'Fill in: Lead source (e.g., Website, Referral, Trade Show), contact details, and any notes.' },
          { type: 'step', text: 'As the lead progresses, update its status using the stage dropdown.' },
          { type: 'step', text: 'When qualified and ready, click "Convert to Opportunity" to move it to the deals pipeline.' },
          { type: 'tip', text: 'Use the Lead Score column to prioritize — higher scores mean the lead shows stronger buying signals.' },
        ]
      },
      {
        heading: 'Menu: Sales → Opportunities',
        content: [
          { type: 'p', text: 'Opportunities (Deals) track active sales from qualified lead to close. The Kanban pipeline gives a visual overview of all deals by stage.' },
          { type: 'list', text: 'Prospect — Initial interest confirmed' },
          { type: 'list', text: 'Proposal — Proposal or quote has been sent' },
          { type: 'list', text: 'Negotiation — Discussing terms' },
          { type: 'list', text: 'Won — Deal closed successfully 🎉' },
          { type: 'list', text: 'Lost — Deal did not close' },
          { type: 'step', text: 'Drag and drop deal cards between columns to advance them through the pipeline.' },
          { type: 'step', text: 'Click any deal card to open its full detail page — linked contact, notes, emails, files, and tasks.' },
          { type: 'tip', text: 'The pipeline header shows the total value of all deals in each stage, perfect for sales forecasting.' },
        ]
      },
      {
        heading: 'Menu: Sales → Accounts',
        content: [
          { type: 'p', text: 'Accounts represent the companies and organizations you do business with. Each Account can have multiple Contacts and multiple Deals linked to it.' },
          { type: 'step', text: 'Click "+ New Account" and enter: Company Name, Website, Industry, Phone, and Address.' },
          { type: 'step', text: 'Link existing Contacts to this Account using the "Contacts" field on the Account profile.' },
          { type: 'p', text: 'The Account profile shows all linked contacts, open opportunities, past deals, support tickets, and emails exchanged — your complete relationship history.' },
        ]
      },
      {
        heading: 'Menu: Sales → Forecasting',
        content: [
          { type: 'p', text: 'The Forecasting view shows projected revenue based on your current pipeline, deal stages, and probability weightings. Use it to predict next month or next quarter\'s sales performance.' },
        ]
      },
      {
        heading: 'Menu: Sales → Contracts',
        content: [
          { type: 'p', text: 'The Contracts section stores and tracks signed agreements with accounts — contract start/end dates, values, and renewal alerts.' },
        ]
      },
      {
        heading: 'Menu: Reports → Report Builder',
        content: [
          { type: 'p', text: 'Build custom reports by choosing any combination of fields (leads, deals, contacts) and applying filters and groupings. Export results as CSV.' },
        ]
      },
      {
        heading: 'Menu: Reports → Revenue Intelligence',
        content: [
          { type: 'p', text: 'Revenue Intelligence provides AI-powered insights into your sales patterns — identifying trends, risks, and opportunities in your pipeline automatically.' },
        ]
      },
      {
        heading: 'Menu: Reports → MRR',
        content: [
          { type: 'p', text: 'The MRR (Monthly Recurring Revenue) dashboard tracks your subscription-based revenue over time — showing growth, churn, and expansion metrics.' },
        ]
      },
      {
        heading: 'Menu: Reports → Scheduler',
        content: [
          { type: 'p', text: 'The CRM Scheduler is a meeting booking tool linked to your contacts and deals — schedule calls and demos without leaving the CRM.' },
        ]
      },
      {
        heading: 'Menu: Configurations → Products',
        content: [
          { type: 'p', text: 'The Products catalog lists items or services you sell, with descriptions, prices, and SKUs. Products can be attached to Opportunities to build accurate quotes.' },
        ]
      },
      {
        heading: 'Menu: Configurations → Assignment Rules',
        content: [
          { type: 'p', text: 'Assignment Rules automatically assign new leads or deals to specific sales reps based on criteria like source, location, or deal size — so no lead falls through the cracks.' },
        ]
      },
      {
        heading: 'Menu: Configurations → Sequences',
        content: [
          { type: 'p', text: 'Email Sequences are automated, multi-step outreach campaigns sent to leads on a scheduled cadence. For example: send an intro email on day 1, a follow-up on day 3, and a final email on day 7.' },
          { type: 'step', text: 'Go to Configurations → Sequences.' },
          { type: 'step', text: 'Click "+ New Sequence" and define each step (email template + delay in days).' },
          { type: 'step', text: 'Enroll leads into the sequence from their Lead profile.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // PROJECTS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'projects',
    title: 'Projects',
    icon: '📋',
    color: 'bg-purple-100 text-purple-600',
    route: '/projects',
    sections: [
      {
        heading: 'What is the Projects Module?',
        content: [
          { type: 'p', text: 'The Projects module is your team\'s work management hub. Organize large initiatives into Projects, break them into Tasks, track everything on a visual Kanban board or Gantt chart, and manage time with built-in timesheets.' },
        ]
      },
      {
        heading: 'Module Menu: Overview',
        content: [
          { type: 'p', text: 'The module-level Overview is a dashboard that summarizes all your projects across the organization:' },
          { type: 'list', text: 'Total projects by status (Active, On Hold, Completed)' },
          { type: 'list', text: 'Tasks due this week across all projects' },
          { type: 'list', text: 'Team workload summary' },
          { type: 'list', text: 'Recent project activity feed' },
        ]
      },
      {
        heading: 'Module Menu: All Projects',
        content: [
          { type: 'p', text: 'A full list of all projects you have access to. Each row shows the project name, status, progress percentage, assignees, and due date.' },
          { type: 'step', text: 'Click "+ New Project" to create one.' },
          { type: 'step', text: 'Fill in: Project Name (required), Description, Start Date, End Date, and optionally a Color tag.' },
          { type: 'step', text: 'Click "Save". Your new project opens automatically.' },
          { type: 'step', text: 'Click any project name in the list to open it and view its tabs.' },
        ]
      },
      {
        heading: 'Module Menu: My Tasks',
        content: [
          { type: 'p', text: 'My Tasks is a personal view that shows every task assigned to you, regardless of which project it belongs to. Filter by status or due date to prioritize your day.' },
          { type: 'tip', text: 'This is the fastest way to see what you personally need to do today without navigating into each project separately.' },
        ]
      },
      {
        heading: 'Module Menu: Timesheets',
        content: [
          { type: 'p', text: 'The module-level Timesheets view shows all logged time entries across all projects for your team. Managers can approve, reject, or export timesheet records.' },
          { type: 'tip', text: 'Use this section at the end of each week to review billable hours and submit for approval.' },
        ]
      },
      {
        heading: 'Module Menu: Workload',
        content: [
          { type: 'p', text: 'The Workload view shows how work is distributed across your team — who is over-capacity and who has bandwidth to take on more. Each team member appears as a row with their total assigned tasks and hours per week.' },
          { type: 'tip', text: 'Use Workload before assigning new tasks to avoid overloading specific team members.' },
        ]
      },
      {
        heading: 'Module Menu: Templates',
        content: [
          { type: 'p', text: 'Project Templates let you create reusable project structures. Instead of setting up the same tasks and phases from scratch every time, you save a template and apply it to future projects instantly.' },
          { type: 'step', text: 'Go to Templates and click "+ New Template".' },
          { type: 'step', text: 'Add standard phases, task lists, and default assignees.' },
          { type: 'step', text: 'When creating a new project, select "Use Template" to pre-populate it.' },
        ]
      },
      {
        heading: 'Project Tabs: Overview',
        content: [
          { type: 'p', text: 'Inside any individual project, the Overview tab shows that project\'s summary: description, progress ring, key dates, linked team members, and recent activity.' },
        ]
      },
      {
        heading: 'Project Tabs: Board (Kanban)',
        content: [
          { type: 'p', text: 'The Board tab is the primary workspace — a visual Kanban board with task columns:' },
          { type: 'list', text: '📌 To Do — Work not yet started' },
          { type: 'list', text: '⚡ In Progress — Actively being worked on (auto-starts time tracking timer)' },
          { type: 'list', text: '👀 In Review — Completed, awaiting approval or review' },
          { type: 'list', text: '✅ Done — Fully completed' },
          { type: 'step', text: 'Click "+ Add Task" in any column to create a task.' },
          { type: 'step', text: 'Drag and drop task cards between columns to update their status.' },
          { type: 'step', text: 'Click any task card to open its full detail panel on the right: description, assignee, dates, comments, and attachments.' },
        ]
      },
      {
        heading: 'Project Tabs: List',
        content: [
          { type: 'p', text: 'The List tab shows all tasks in a flat, sortable, spreadsheet-style table. Useful for managers who want to see all tasks at once without the visual column layout.' },
          { type: 'tip', text: 'You can sort tasks by due date, priority, or assignee by clicking the column headers.' },
        ]
      },
      {
        heading: 'Project Tabs: Gantt',
        content: [
          { type: 'p', text: 'The Gantt chart shows all tasks plotted on a timeline, visualizing their start dates, durations, and dependencies. Ideal for project planning and identifying bottlenecks.' },
          { type: 'step', text: 'Click and drag the right edge of any task bar on the Gantt chart to extend or shorten its duration.' },
          { type: 'step', text: 'Drag a task bar horizontally to change its start date.' },
        ]
      },
      {
        heading: 'Project Tabs: Milestones',
        content: [
          { type: 'p', text: 'Milestones are key checkpoints in your project — moments that mark significant progress (e.g., "Beta Launch", "Client Handover", "Phase 1 Complete").' },
          { type: 'step', text: 'Click "+ Add Milestone", enter a name, and set its due date.' },
          { type: 'p', text: 'Milestones appear as full-day 🏁 banners on the Calendar module.' },
        ]
      },
      {
        heading: 'Project Tabs: Sprints',
        content: [
          { type: 'p', text: 'For teams following Agile methodology, Sprints let you group tasks into time-boxed work cycles (e.g., 2-week sprints). Create a sprint, add tasks to it, and track sprint progress separately from the full project backlog.' },
        ]
      },
      {
        heading: 'Project Tabs: Timesheets',
        content: [
          { type: 'p', text: 'The project-level Timesheets tab shows all time logged against tasks within this specific project — who worked on what, for how long, and whether the hours are billable.' },
          { type: 'step', text: 'Open any task and click "Log Time" to manually record hours.' },
          { type: 'step', text: 'Or use the auto-timer: when a task moves to "In Progress" on the board, the timer starts automatically.' },
        ]
      },
      {
        heading: 'Project Tabs: Reports',
        content: [
          { type: 'p', text: 'Project-level reports show completion rate, hours burned vs. estimated, task breakdown by status and assignee, and timeline adherence for this specific project.' },
        ]
      },
      {
        heading: 'Project Tabs: Settings',
        content: [
          { type: 'p', text: 'The project Settings tab lets you edit the project name, description, dates, color, and access permissions. You can also archive or delete the project here.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CALENDAR
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'calendar',
    title: 'Calendar',
    icon: '📅',
    color: 'bg-orange-100 text-orange-600',
    route: '/calendar',
    sections: [
      {
        heading: 'What is the Calendar?',
        content: [
          { type: 'p', text: 'The Calendar is your unified scheduling view. It automatically pulls in all project tasks (that have dates set) and displays them alongside your manually created events — giving you one complete picture of your week.' },
        ]
      },
      {
        heading: 'View Controls (Top Left)',
        content: [
          { type: 'p', text: 'Switch between different time perspectives using the view dropdown:' },
          { type: 'list', text: 'Day — See a single day in hour-by-hour detail' },
          { type: 'list', text: 'Week (default) — The standard 7-day grid view, one column per day' },
          { type: 'list', text: 'Month — A traditional monthly calendar overview' },
          { type: 'list', text: 'Year — A 12-month bird\'s-eye view to see busy vs. quiet periods' },
          { type: 'step', text: 'Use the ‹ and › arrows to navigate backward and forward through time.' },
          { type: 'step', text: 'Click "Today" to jump back to the current date instantly.' },
        ]
      },
      {
        heading: 'Filters (Top Right)',
        content: [
          { type: 'p', text: 'Two filter dropdowns help you declutter the calendar when it gets busy:' },
          { type: 'list', text: '"All Items" dropdown → switch to "Tasks Only" to hide personal events, or "Events Only" to hide project tasks' },
          { type: 'list', text: '"All Projects" dropdown → select a specific project to only show its tasks and milestones on the calendar' },
          { type: 'tip', text: 'When presenting your schedule to a manager, use the Project filter to show only the work relevant to one initiative.' },
        ]
      },
      {
        heading: 'Dynamic Color Coding',
        content: [
          { type: 'p', text: 'The calendar automatically assigns a distinct color to each of your active projects. All tasks from "Project A" share one color, tasks from "Project B" share another.' },
          { type: 'list', text: 'Project-linked tasks — Colored by their project' },
          { type: 'list', text: 'Generic events (no project) — Slate gray' },
          { type: 'list', text: 'Project milestones — Full-day banners with 🏁' },
          { type: 'tip', text: 'Color coding lets you recognize which project a block belongs to without reading the title.' },
        ]
      },
      {
        heading: 'All-Day Events Row',
        content: [
          { type: 'p', text: 'The row at the very top of the weekly grid (above the hour slots) shows all-day events and project milestones — items that span a full day rather than a specific time slot.' },
        ]
      },
      {
        heading: 'Creating Events from the Canvas',
        content: [
          { type: 'step', text: 'Click on any empty time slot on the calendar grid at the time you want.' },
          { type: 'step', text: 'A "Create New" modal appears. Choose the tab:' },
          { type: 'list', text: 'Event tab — A general appointment or meeting not linked to a project' },
          { type: 'list', text: 'Task tab — A project task that will also appear on the project board' },
          { type: 'step', text: 'For a Task: select the required Project from the dropdown, optionally add an Assignee, and type the title.' },
          { type: 'step', text: 'Verify Start Time and End Time (pre-filled from where you clicked).' },
          { type: 'step', text: 'Tick "All Day" for events that span the full day.' },
          { type: 'step', text: 'Click "Save". The item appears immediately on the grid.' },
        ]
      },
      {
        heading: 'Drag to Reschedule',
        content: [
          { type: 'step', text: 'Click and hold the center of any event or task card.' },
          { type: 'step', text: 'Drag it to the new time slot or a different day.' },
          { type: 'step', text: 'Release — the new time is saved instantly.' },
          { type: 'tip', text: 'Moving a task automatically updates its due date in the Projects module — no double-entry needed.' },
        ]
      },
      {
        heading: 'Drag to Resize (Change Duration)',
        content: [
          { type: 'step', text: 'Hover over the bottom edge of any event card.' },
          { type: 'step', text: 'When your cursor changes to a resize arrow, click and drag downward.' },
          { type: 'step', text: 'Stretch the card to the desired end time, then release.' },
          { type: 'p', text: 'The new duration is saved automatically.' },
        ]
      },
      {
        heading: 'Editing Events and Tasks',
        content: [
          { type: 'step', text: 'Click any existing event or task card on the calendar.' },
          { type: 'step', text: 'An edit modal opens showing the full details.' },
          { type: 'step', text: 'Change the title, time, project, or assignee as needed.' },
          { type: 'step', text: 'Click "Save" to apply the changes.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // HUDDLE
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'huddle',
    title: 'Huddle',
    icon: '🎙️',
    color: 'bg-pink-100 text-pink-600',
    route: '/huddle',
    sections: [
      {
        heading: 'What is Huddle?',
        content: [
          { type: 'p', text: 'Huddle is your team\'s integrated meeting space. It is designed to keep conversations focused and connected to your actual work. Every Huddle can be linked to a project, so team members can see live task statuses without leaving the meeting.' },
        ]
      },
      {
        heading: 'The Huddle Dashboard',
        content: [
          { type: 'p', text: 'The main Huddle page shows a list of all huddles:' },
          { type: 'list', text: 'Upcoming — Scheduled meetings not yet started' },
          { type: 'list', text: 'Active — Meetings currently in progress (shown at the top with a live indicator)' },
          { type: 'list', text: 'Past — Completed meetings with notes and action items archived' },
          { type: 'step', text: 'Click any huddle card to open its room.' },
        ]
      },
      {
        heading: 'Creating a Huddle',
        content: [
          { type: 'step', text: 'Click "+ New Huddle" on the Huddle main page.' },
          { type: 'step', text: 'Enter the Huddle Title (e.g., "Weekly Sprint Review").' },
          { type: 'step', text: 'Set the Date and Time.' },
          { type: 'step', text: 'Use the "Linked Project" dropdown to connect this huddle to a project — this imports live task data into the room.' },
          { type: 'step', text: 'Select Attendees by adding team members.' },
          { type: 'step', text: 'Click "Create". All attendees receive a notification with the meeting details.' },
        ]
      },
      {
        heading: 'Inside the Huddle Room',
        content: [
          { type: 'p', text: 'When you open a Huddle, you enter the meeting room which contains:' },
          { type: 'list', text: 'Participant Panel — See who has joined the huddle' },
          { type: 'list', text: 'Project Context Panel — If a project is linked, shows the current task board (To Do, In Progress, Done) without needing to switch tabs' },
          { type: 'list', text: 'Shared Notes — A collaborative notepad visible to all participants. Type meeting minutes, decisions, and key points in real time.' },
          { type: 'list', text: 'Chat — A live text chat for quick side-comments during the meeting' },
        ]
      },
      {
        heading: 'Creating Action Items During a Huddle',
        content: [
          { type: 'p', text: 'The most powerful Huddle feature: any follow-up action raised during the meeting can be instantly turned into a real project task.' },
          { type: 'step', text: 'During the meeting, click the "+ Action Item" or "+ Task" button inside the Huddle room.' },
          { type: 'step', text: 'Type the task title, assign it to a team member, and set a due date.' },
          { type: 'step', text: 'Click "Create". The task immediately appears in the linked project\'s Kanban board.' },
          { type: 'tip', text: 'Action items created in a Huddle will automatically appear on the assigned team member\'s Calendar!' },
        ]
      },
      {
        heading: 'Post-Huddle: Notes and Summary',
        content: [
          { type: 'p', text: 'After a Huddle ends, the meeting notes and all action items are saved and accessible from the Past Huddles list. Use this to review what was decided and track whether action items were completed.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // KNOWLEDGE BASE
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'knowledge',
    title: 'Knowledge Base',
    icon: '📚',
    color: 'bg-yellow-100 text-yellow-600',
    route: '/knowledge-system',
    sections: [
      {
        heading: 'What is the Knowledge Base?',
        content: [
          { type: 'p', text: 'The Knowledge Base is a library of written articles, guides, and FAQs. It serves two audiences: your internal team (for process documentation) and your customers via a branded public self-service portal.' },
        ]
      },
      {
        heading: 'Menu: Dashboard',
        content: [
          { type: 'p', text: 'The Knowledge Base Dashboard shows article performance stats:' },
          { type: 'list', text: 'Total published, draft, and archived articles' },
          { type: 'list', text: 'Most-viewed articles this month' },
          { type: 'list', text: 'Articles with high search-no-result rates (topics customers search for but can\'t find)' },
        ]
      },
      {
        heading: 'Menu: Articles (List & Grid View)',
        content: [
          { type: 'p', text: 'The main Articles section shows all your content. Toggle between List View (table format) and Grid View (card format) using the icons in the top right.' },
          { type: 'list', text: 'Filter by Status: All, Published, Draft, or Archived' },
          { type: 'list', text: 'Filter by Category to find articles in a specific section' },
          { type: 'list', text: 'Use the search bar to find articles by keyword' },
          { type: 'step', text: 'Click any article to open it and read the full content.' },
          { type: 'step', text: 'Click the ✏️ Edit button on any article to modify it.' },
        ]
      },
      {
        heading: 'Creating a New Article',
        content: [
          { type: 'step', text: 'Click "+ New Article".' },
          { type: 'step', text: 'Enter a clear, descriptive Title — this is what customers and search engines see first.' },
          { type: 'step', text: 'Use the rich text editor to write the article body. Available formatting tools include: headings (H1/H2/H3), bold, italic, bullet lists, numbered lists, links, images, and code blocks.' },
          { type: 'step', text: 'Set the Category (e.g., "Getting Started", "Billing", "Technical Issues").' },
          { type: 'step', text: 'Add Tags to improve searchability.' },
          { type: 'step', text: 'Toggle Visibility: "Internal Only" (team only) or "Public" (visible on customer portal).' },
          { type: 'step', text: 'Click "Save as Draft" to save without publishing, or "Publish" to make it live immediately.' },
          { type: 'tip', text: 'Always save as Draft first and review the article from the customer\'s perspective before publishing.' },
        ]
      },
      {
        heading: 'Menu: Categories',
        content: [
          { type: 'p', text: 'Categories organize your articles into logical sections. Customers browsing your public portal see articles grouped by category.' },
          { type: 'step', text: 'Go to Categories from the left navigation.' },
          { type: 'step', text: 'Click "+ New Category", enter a name (e.g., "Account Setup"), an optional icon, and a description.' },
          { type: 'step', text: 'Drag categories to reorder how they appear on the public portal.' },
        ]
      },
      {
        heading: 'Menu: Tags',
        content: [
          { type: 'p', text: 'Tags are keywords attached to articles to improve searchability. When a customer searches for "password reset", any article tagged with "password" or "login" will surface.' },
          { type: 'step', text: 'Go to Tags to see all existing tags and how many articles use each one.' },
          { type: 'step', text: 'Delete unused tags to keep the list clean.' },
        ]
      },
      {
        heading: 'The Public Portal',
        content: [
          { type: 'p', text: 'All Published articles with "Public" visibility appear automatically on your branded customer-facing help center portal. Customers can browse categories, search for articles, and find answers 24/7 without needing to contact your support team.' },
          { type: 'tip', text: 'A well-maintained Knowledge Base significantly reduces your support ticket volume — because customers can self-serve.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // MARKETING
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'marketing',
    title: 'Marketing',
    icon: '📣',
    color: 'bg-red-100 text-red-600',
    route: '/marketing',
    sections: [
      {
        heading: 'What is the Marketing Module?',
        content: [
          { type: 'p', text: 'The Marketing module lets you plan, design, send, and analyze outreach campaigns. Reach your customers and leads through targeted email campaigns, track performance in real time, and manage your entire contact audience in one place.' },
        ]
      },
      {
        heading: 'Menu: Dashboard',
        content: [
          { type: 'p', text: 'The Marketing Dashboard shows a summary of all your campaign activity:' },
          { type: 'list', text: 'Total emails sent this month' },
          { type: 'list', text: 'Average open rate and click-through rate' },
          { type: 'list', text: 'Active campaigns and their current status' },
          { type: 'list', text: 'Audience growth over time' },
        ]
      },
      {
        heading: 'Menu: Campaigns',
        content: [
          { type: 'p', text: 'Campaigns are your outreach sends — a batch of emails sent to a selected audience with a specific message.' },
          { type: 'step', text: 'Click "+ New Campaign".' },
          { type: 'step', text: 'Enter the Campaign Name and Email Subject Line.' },
          { type: 'step', text: 'Select the Template you want to use.' },
          { type: 'step', text: 'Choose the Audience (which contact segment should receive this).' },
          { type: 'step', text: 'Schedule the send date and time, or click "Send Now".' },
          { type: 'step', text: 'Confirm and launch.' },
          { type: 'tip', text: 'Always send a test email to yourself before launching to check formatting on different email clients.' },
        ]
      },
      {
        heading: 'Campaign Analytics',
        content: [
          { type: 'p', text: 'After a campaign is sent, click it to view detailed analytics:' },
          { type: 'list', text: 'Open Rate — What % of recipients opened the email' },
          { type: 'list', text: 'Click Rate — What % clicked at least one link' },
          { type: 'list', text: 'Bounce Rate — What % of emails failed to deliver' },
          { type: 'list', text: 'Unsubscribe Rate — What % opted out after this send' },
          { type: 'list', text: 'Individual activity — See exactly who opened, clicked, or unsubscribed' },
        ]
      },
      {
        heading: 'Menu: Campaign Calendar',
        content: [
          { type: 'p', text: 'The Campaign Calendar shows all your scheduled and sent campaigns on a monthly calendar. This is useful for planning your marketing content calendar and ensuring you are not overloading your audience.' },
        ]
      },
      {
        heading: 'Menu: Templates',
        content: [
          { type: 'p', text: 'Templates are reusable email designs. Create them once and use them across many campaigns.' },
          { type: 'step', text: 'Click "+ New Template".' },
          { type: 'step', text: 'Choose a starting layout: Blank, Single Column, Newsletter, or Announcement.' },
          { type: 'step', text: 'Use the visual drag-and-drop editor to add content blocks: text, images, buttons, dividers, and social links.' },
          { type: 'step', text: 'Customize colors and fonts to match your brand.' },
          { type: 'step', text: 'Click "Save Template".' },
          { type: 'tip', text: 'Create a branded master template for your company and use it as the base for all campaigns — ensuring visual consistency.' },
        ]
      },
      {
        heading: 'Menu: Audience (Contacts)',
        content: [
          { type: 'p', text: 'The Audience section manages the list of contacts who can receive your marketing emails. This is a marketing-specific list, separate from your CRM contacts.' },
          { type: 'list', text: 'Import — Upload a CSV file of contacts' },
          { type: 'list', text: 'Add Manually — Enter a contact\'s email, name, and any custom fields one by one' },
          { type: 'list', text: 'Segments — Group contacts by shared attributes (e.g., "Customers from UAE", "Free Tier Users") for targeted campaigns' },
          { type: 'tip', text: 'Keep your audience list clean: regularly remove bounced or unsubscribed contacts to protect your sender reputation.' },
        ]
      },
      {
        heading: 'Menu: Personas',
        content: [
          { type: 'p', text: 'Personas are fictional representations of your ideal customer types. Defining them helps your team write more targeted, relevant campaign content.' },
        ]
      },
      {
        heading: 'Menu: Scoring Rules',
        content: [
          { type: 'p', text: 'Scoring Rules assign points to contacts based on behaviors (e.g., opening an email = +5 points, clicking a link = +10 points). High-scoring contacts can be automatically surfaced as hot leads in the CRM.' },
        ]
      },
      {
        heading: 'Menu: Dynamic Content',
        content: [
          { type: 'p', text: 'Dynamic Content lets you personalize email content based on the recipient\'s data — for example, showing different text to customers in different countries, or addressing them by first name.' },
        ]
      },
      {
        heading: 'Menu: Frequency Capping',
        content: [
          { type: 'p', text: 'Frequency Capping limits how many marketing emails a single contact can receive within a time window (e.g., no more than 3 emails per week). This prevents over-communication and protects against unsubscribes.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CSAT
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'csat',
    title: 'CSAT',
    icon: '⭐',
    color: 'bg-amber-100 text-amber-600',
    route: '/csat',
    sections: [
      {
        heading: 'What is CSAT?',
        content: [
          { type: 'p', text: 'CSAT (Customer Satisfaction Score) is a feedback mechanism that automatically surveys customers after their support interactions. The responses are aggregated into a single satisfaction percentage that tells you how your customers feel about your service.' },
        ]
      },
      {
        heading: 'The CSAT Score',
        content: [
          { type: 'p', text: 'At the top of the CSAT module, you see your overall CSAT score as a large percentage number.' },
          { type: 'list', text: '90–100% — Excellent. Your customers are very happy.' },
          { type: 'list', text: '70–89% — Good. Room for improvement.' },
          { type: 'list', text: 'Below 70% — Needs attention. Review negative responses immediately.' },
          { type: 'tip', text: 'Industry benchmark is around 85%. Aim to stay above this.' },
        ]
      },
      {
        heading: 'Viewing Individual Responses',
        content: [
          { type: 'p', text: 'Below the overall score, you can see every individual survey response:' },
          { type: 'list', text: 'Customer name and the ticket they were surveyed about' },
          { type: 'list', text: 'Their rating: 😊 Satisfied, 😐 Neutral, or 😞 Dissatisfied' },
          { type: 'list', text: 'Any written comment they left explaining their rating' },
          { type: 'list', text: 'The agent who handled the ticket' },
          { type: 'step', text: 'Click on any response to view the original ticket and understand the context.' },
          { type: 'tip', text: 'Read every Dissatisfied response carefully — they often reveal systemic issues in your support process.' },
        ]
      },
      {
        heading: 'Filtering Responses',
        content: [
          { type: 'p', text: 'Filter the responses by:' },
          { type: 'list', text: 'Date range — this week, this month, last quarter' },
          { type: 'list', text: 'Rating — show only Dissatisfied responses to focus on issues' },
          { type: 'list', text: 'Agent — see how customers rate a specific team member' },
        ]
      },
      {
        heading: 'Configuring CSAT Surveys',
        content: [
          { type: 'p', text: 'Admins can configure the survey behavior from Settings:' },
          { type: 'list', text: 'Trigger — when to send the survey (e.g., automatically when a ticket is marked Resolved)' },
          { type: 'list', text: 'Delay — wait X hours after resolution before sending' },
          { type: 'list', text: 'Survey message — customize the text that appears in the survey email' },
          { type: 'list', text: 'Rating scale — 3-point (😊 😐 😞) or 5-star' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // APPOINTMENTS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'appointments',
    title: 'Appointments',
    icon: '🗓️',
    color: 'bg-teal-100 text-teal-600',
    route: '/appointments',
    sections: [
      {
        heading: 'What is Appointments?',
        content: [
          { type: 'p', text: 'The Appointments module is a built-in scheduling tool — like Calendly, built into XenBox. Team members publish their availability, share a personal booking link, and let customers or partners book meetings automatically. All bookings sync to the Calendar.' },
        ]
      },
      {
        heading: 'Setting Your Availability',
        content: [
          { type: 'step', text: 'Navigate to Appointments in the left sidebar.' },
          { type: 'step', text: 'Click on "My Availability" or "Settings".' },
          { type: 'step', text: 'Select which days of the week you are available (e.g., Monday to Friday).' },
          { type: 'step', text: 'Set your daily working hours (e.g., 9:00 AM to 5:00 PM).' },
          { type: 'step', text: 'Set the default meeting duration (15 min, 30 min, or 60 min).' },
          { type: 'step', text: 'Set a minimum notice period (e.g., require at least 2 hours notice before a meeting can be booked).' },
          { type: 'step', text: 'Save. Your booking page is now live.' },
        ]
      },
      {
        heading: 'Your Booking Link',
        content: [
          { type: 'p', text: 'Each team member gets a unique personal booking URL. When someone visits this URL, they see only your available time slots and can book a meeting in seconds — without any back-and-forth emails.' },
          { type: 'step', text: 'Find your booking link in the Appointments section.' },
          { type: 'step', text: 'Copy it and add it to your email signature, your LinkedIn profile, or share it directly with clients.' },
          { type: 'tip', text: 'A booking link in your email signature turns every email you send into a call-scheduling opportunity.' },
        ]
      },
      {
        heading: 'Managing Bookings',
        content: [
          { type: 'p', text: 'The main Appointments page lists all your upcoming confirmed bookings:' },
          { type: 'list', text: 'Booking name and the booker\'s contact details' },
          { type: 'list', text: 'Date and time of the meeting' },
          { type: 'list', text: 'Notes the booker left when scheduling' },
          { type: 'list', text: 'Status: Confirmed, Completed, or Cancelled' },
          { type: 'tip', text: 'All confirmed bookings also appear automatically on your Calendar in the Calendar module.' },
        ]
      },
      {
        heading: 'Cancelling or Rescheduling a Booking',
        content: [
          { type: 'step', text: 'Find the booking in your Appointments list.' },
          { type: 'step', text: 'Click on it to open the details.' },
          { type: 'step', text: 'Click "Cancel" or "Reschedule" as needed.' },
          { type: 'p', text: 'The booker will automatically receive an email notification about the change.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // NEXUS AI
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'nexus',
    title: 'Nexus AI',
    icon: '🧠',
    color: 'bg-violet-100 text-violet-600',
    route: '/nexus',
    sections: [
      {
        heading: 'What is Nexus AI?',
        content: [
          { type: 'p', text: 'Nexus AI is XenBox\'s intelligent automation layer. You can create autonomous AI agents that handle repetitive tasks — answering FAQs, routing tickets, summarizing conversations, qualifying leads, drafting email responses — without any manual work from your team.' },
        ]
      },
      {
        heading: 'Menu: Dashboard (Nexus Overview)',
        content: [
          { type: 'p', text: 'The Nexus Dashboard gives you an overview of all your AI agents and their activity:' },
          { type: 'list', text: 'Total agents running vs. paused' },
          { type: 'list', text: 'Tasks completed today by AI agents' },
          { type: 'list', text: 'Pending approvals waiting for your review' },
          { type: 'list', text: 'Recent AI actions log' },
        ]
      },
      {
        heading: 'Menu: Agents',
        content: [
          { type: 'p', text: 'Agents are AI workers configured to perform a specific role. Each agent has a defined purpose, a set of tools it can use, and rules about when and how it acts.' },
          { type: 'step', text: 'Go to Nexus → Agents.' },
          { type: 'step', text: 'Click "+ New Agent".' },
          { type: 'step', text: 'Give the agent a name (e.g., "Tier 1 Support Bot") and describe its role in plain English in the Instructions field.' },
          { type: 'step', text: 'Assign Skills (capabilities the agent can use — see Menu: Skills below).' },
          { type: 'step', text: 'Set the Trigger — when should this agent activate? (e.g., "when a new ticket is created with priority Low").' },
          { type: 'step', text: 'Toggle the agent to Active. It will now run automatically based on its trigger.' },
          { type: 'tip', text: 'Start with a simple, narrow scope for your first agent — for example, an agent that only auto-replies to "password reset" tickets using a Knowledge Base article.' },
        ]
      },
      {
        heading: 'Menu: Skills',
        content: [
          { type: 'p', text: 'Skills are specific capabilities you grant to an Agent. Each Skill represents one action the AI is allowed to perform:' },
          { type: 'list', text: '"Search Knowledge Base" — Look up articles to find answers' },
          { type: 'list', text: '"Read Ticket Data" — Access the details of a support ticket' },
          { type: 'list', text: '"Send Email Reply" — Send an email on your behalf' },
          { type: 'list', text: '"Create Task in Projects" — Add a task to a project board' },
          { type: 'list', text: '"Update CRM Lead" — Change the status or fields of a CRM lead' },
          { type: 'p', text: 'Assign only the skills an agent genuinely needs — following the principle of least privilege.' },
        ]
      },
      {
        heading: 'Menu: Teams',
        content: [
          { type: 'p', text: 'Nexus Teams group multiple AI Agents together to collaborate on complex workflows. For example, a "Support Team" might have one agent for triage, one for answering FAQs, and one for escalating unresolved tickets.' },
        ]
      },
      {
        heading: 'Menu: Tools',
        content: [
          { type: 'p', text: 'Tools are the integrations and API connections available to your Agents. Your admin can add new tools (e.g., a connection to your internal database, a third-party API) which agents can then be granted permission to use.' },
        ]
      },
      {
        heading: 'Menu: Vault (Credentials)',
        content: [
          { type: 'p', text: 'The Vault is a secure store for API keys, passwords, and credentials that your Agents need to connect to external systems. Credentials stored here are encrypted and never exposed to users — Agents reference them by name only.' },
          { type: 'warning', text: 'Only Super Admins and trusted Admins should have access to the Vault. Treat credentials here with the same care as passwords.' },
        ]
      },
      {
        heading: 'Menu: Approvals',
        content: [
          { type: 'p', text: 'For high-risk actions (like sending emails, closing tickets, or modifying CRM data), you can require a human to approve the action before the agent executes it.' },
          { type: 'step', text: 'Go to Nexus → Approvals.' },
          { type: 'step', text: 'Review the list of actions pending your approval.' },
          { type: 'step', text: 'Click "Approve" to allow the agent to proceed, or "Reject" to block it with a reason.' },
          { type: 'tip', text: 'Approvals are your safety net — they let you scale with AI while keeping humans in the loop for consequential decisions.' },
        ]
      },
      {
        heading: 'Menu: Runs',
        content: [
          { type: 'p', text: 'The Runs log is a complete audit trail of every action taken by every AI agent — what triggered it, what it did, what data it accessed, and the outcome. Use this to verify agents are working correctly.' },
          { type: 'step', text: 'Click any run entry to expand its full step-by-step trace.' },
        ]
      },
      {
        heading: 'Menu: Observability',
        content: [
          { type: 'p', text: 'Observability provides performance metrics across all your agents over time:' },
          { type: 'list', text: 'Tasks completed per agent' },
          { type: 'list', text: 'Average task duration' },
          { type: 'list', text: 'Error rate and failure types' },
          { type: 'list', text: 'Approval wait times' },
        ]
      },
      {
        heading: 'Menu: Benchmark',
        content: [
          { type: 'p', text: 'Benchmark lets you evaluate and compare the performance of different AI models or agent configurations on a standardized set of test tasks. Use it to determine which setup produces the best results before deploying to production.' },
        ]
      },
      {
        heading: 'Menu: Settings (Nexus)',
        content: [
          { type: 'p', text: 'Nexus-specific settings include: selecting your preferred AI model (e.g., GPT-4o, Gemini), setting default behaviors, configuring rate limits, and managing which users can create or modify agents.' },
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // SETTINGS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'settings',
    title: 'Settings',
    icon: '⚙️',
    color: 'bg-slate-100 text-slate-600',
    route: '/settings',
    sections: [
      {
        heading: 'What is Settings?',
        content: [
          { type: 'p', text: 'Settings is the platform administration area. Company Admins and Super Admins configure everything here — from company branding and user management to email configuration and AI engine settings. Standard users may have read-only or no access to this section.' },
        ]
      },
      {
        heading: 'Menu: Overview',
        content: [
          { type: 'p', text: 'The Settings Overview shows a summary of your current configuration — company name, active users, installed apps, and quick links to common admin tasks.' },
        ]
      },
      {
        heading: 'Menu: Company',
        content: [
          { type: 'p', text: 'Company settings affect how the platform appears and behaves for everyone in your organization:' },
          { type: 'list', text: 'Company Name — Displayed in the sidebar and on customer-facing portals' },
          { type: 'list', text: 'Company Logo — Shown in emails, reports, and the public portal' },
          { type: 'list', text: 'Address and Contact Details' },
          { type: 'list', text: 'Default Timezone — Affects how times display for all users' },
          { type: 'list', text: 'Default Language — Sets the starting language for new users' },
          { type: 'step', text: 'Make your changes and click "Save".' },
          { type: 'tip', text: 'Your company logo is used in all outbound emails, so make sure it looks sharp and is in PNG or SVG format.' },
        ]
      },
      {
        heading: 'Menu: Users',
        content: [
          { type: 'p', text: 'The Users page lists everyone with access to your XenBox workspace.' },
          { type: 'step', text: 'Click "+ Invite User" to add someone new.' },
          { type: 'step', text: 'Enter their email address.' },
          { type: 'step', text: 'Assign them to one or more Permission Groups (see Roles below).' },
          { type: 'step', text: 'Click "Send Invite". They get an email with a link to set up their account and choose a password.' },
          { type: 'step', text: 'To remove a user, click their name and select "Deactivate". Deactivated users cannot log in but their historical data is preserved.' },
          { type: 'warning', text: 'Assigning Admin-level permissions gives the user access to billing, all settings, and the ability to delete data. Only do this for trusted personnel.' },
        ]
      },
      {
        heading: 'Roles and Permission Groups',
        content: [
          { type: 'p', text: 'Each user is assigned to Permission Groups that control what they can access and do:' },
          { type: 'list', text: '🔴 Super Admin — Full access to everything including billing and user deletion (your IT administrator)' },
          { type: 'list', text: '🟠 Company Admin — Full platform access except billing. Can invite/remove users and configure all modules.' },
          { type: 'list', text: '🟡 Helpdesk Manager — Full access to Helpdesk: can create SLAs, categories, teams, and view all reports.' },
          { type: 'list', text: '🔵 Helpdesk Agent — Can view and respond to tickets only.' },
          { type: 'list', text: '🟢 CRM Manager — Full CRM access including reports, configuration, and all records.' },
          { type: 'list', text: '⚪ Standard User — Module-by-module access. Can use what they are assigned to, but cannot configure settings.' },
        ]
      },
      {
        heading: 'Menu: Integrations',
        content: [
          { type: 'p', text: 'Connect XenBox to your existing tools and services:' },
          { type: 'list', text: 'Email Providers — Configure the mailbox that receives customer support emails' },
          { type: 'list', text: 'Chat Widget — Get an embed code to add a live chat bubble to your website' },
          { type: 'list', text: 'Third-party Apps — Connect to Slack, WhatsApp, and other channels' },
          { type: 'step', text: 'Go to Settings → Integrations, choose the service, and follow the setup wizard.' },
        ]
      },
      {
        heading: 'Menu: Configuration → AI Engine',
        content: [
          { type: 'p', text: 'The AI Engine settings control the AI features used throughout the platform — auto-suggestions, ticket summarization, and Nexus AI models. Admins can select which AI model to use and configure API keys here.' },
        ]
      },
      {
        heading: 'Menu: Configuration → Resource Calendars',
        content: [
          { type: 'p', text: 'Resource Calendars define working hours, public holidays, and non-working days for your organization or specific teams. They are used by the Appointments module to accurately show availability and by SLAs to calculate response time correctly (e.g., a ticket logged at 11 PM on Friday should not breach its SLA until Monday morning).' },
          { type: 'step', text: 'Go to Configuration → Resource Calendars.' },
          { type: 'step', text: 'Click the default calendar to edit working hours and add public holiday dates.' },
          { type: 'step', text: 'Create additional calendars for different teams or locations if they have different working schedules.' },
        ]
      },
      {
        heading: 'Services: Email Services',
        content: [
          { type: 'p', text: 'The Email Services section in the sidebar manages all outbound email configuration:' },
          { type: 'list', text: 'Configuration — Set up SMTP/email provider credentials for sending emails' },
          { type: 'list', text: 'Templates — Create reusable email templates for system notifications (ticket confirmations, CSAT surveys, etc.)' },
          { type: 'list', text: 'Logs — View a history of every email sent by the system, with delivery status' },
        ]
      },
      {
        heading: 'Services: App Store',
        content: [
          { type: 'p', text: 'The App Store is where you activate or deactivate platform modules. Not all modules are turned on by default — if you do not see a module in your sidebar, it may need to be enabled here by an Admin.' },
          { type: 'step', text: 'Go to Settings → App Store.' },
          { type: 'step', text: 'Browse available modules (e.g., Projects, Marketing, CSAT, Inbox).' },
          { type: 'step', text: 'Click "Install" next to any module you want to enable.' },
          { type: 'step', text: 'It will immediately appear in the sidebar for all users with the appropriate permissions.' },
        ]
      },
    ]
  },
];

export default helpDocs;
